import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from "reactstrap";
import { toast } from 'react-toastify';

import "./ccbill-modal-custom.css";
import {
  Form,
  useFormValidation
} from '../../../shared/components/form';
import { createAddCardSchema } from '../../../shared/validations/ccbill';
import PaymentLogos from './PaymentLogos';
import {
  fetchAccountData,
  getDefaultFormValues,
  loadCCBillWidget,
  initializeFormWithUserData,
  createCCBillPaymentToken,
  saveCardToBackend
} from '../../../shared/utils/ccbillFormUtils';
import BillingAddressForm from './BillingAddressForm';
import CardDetailsForm from './CardDetailsForm';
import { reportError } from '../../../shared/sentry';


const AddCardModal = ({
  openModal,
  toggleModal,
  onSuccess,
  existingCardsCount = 0,
  isEditMode = false,
  editCardData = null,
  onEditSubmit = null
}) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [accountData, setAccountData] = useState(null);
  const cardAddedRef = useRef(false);

  const form = useForm({
    resolver: zodResolver(createAddCardSchema(isEditMode)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getDefaultFormValues()
  });

  const {
    validateAndFocus,
    shouldShowError,
  } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  // Reset all state when modal opens
  useEffect(() => {
    if (openModal) {
      // Reset all state variables
      setMessage('');
      setLoading(false);

      // Clear form errors
      form.clearErrors();
    }
  }, [openModal, form]);


  // Initialize form data
  useEffect(() => {
    if (openModal) {
      const loadData = async () => {
        const accountData = await fetchAccountData();
        setAccountData(accountData);

        // Load CCBill Advanced Widget
        loadCCBillWidget();

        initializeFormWithUserData(form, accountData, {
          isEditMode,
          editCardData
        }).catch((err) => {
          reportError(err, 'AddCardModal init form');
        });
      }

      loadData();
    }
  }, [openModal, isEditMode, editCardData, form]);


  // Handle form submission
  const onSubmit = async (data) => {
    setMessage('');

    // If in edit mode, handle edit submission
    if (isEditMode) {
      setLoading(true);
      try {
        const editData = {
          country: data.country,
          city: data.city,
          state: data.state
        };
        await onEditSubmit(editData);
        setLoading(false);
        if (toggleModal) toggleModal();
      } catch (error) {
        setMessage('❌ Failed to update card information');
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      // Create CCBill payment token
      const tokenResult = await createCCBillPaymentToken();
      if (!tokenResult.success) {
        setMessage(`❌ ${tokenResult.error}`);
        setLoading(false);
        return;
      }

      // Save card to backend
      const saveResult = await saveCardToBackend({
        cardNumber: data.cardNumber,
        expMonth: data.expMonth,
        expYear: data.expYear,
        paymentTokenId: tokenResult.paymentTokenId,
        firstName: data?.firstName || '',
        lastName: data?.lastName || '',
        country: data.country,
        stateCode: data.state
      }, { existingCardsCount });

      if (!saveResult.success) {
        setMessage(`❌ ${saveResult.error || 'Failed to add card.'}`);
        setLoading(false);
        return;
      }

      setMessage('✅ Card added successfully!');
      setLoading(false);
      cardAddedRef.current = true;

      if (toggleModal) toggleModal();
      if (onSuccess) onSuccess();
    } catch (error) {
      reportError(error, 'AddCardModal add card');
      setMessage('❌ Failed to add card.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateAndFocus();
    if (isValid) {
      form.handleSubmit(onSubmit)();
    } else {
      toast.error('❌ Please fix the errors in the form');
    }
  };

  const handleCloseButton = () => {
    if (toggleModal) toggleModal();
  };

  const watchedValues = form.watch();

  return (
    <Modal
      isOpen={openModal}
      toggle={handleCloseButton}
      centered
      backdrop={false}
      className="cc-bil-modal"
      contentClassName="ccbill-modal-container"
    >
      <div className="ccbill-modal-content">
        <Form {...form}>
          <form onSubmit={handleSubmit} className="ccbill-form">
            <h3 className="ccbill-form-title text-center">
              {isEditMode ? 'Edit Card' : 'Add Card'}
            </h3>
            <p className="payment-info-text mb-5">
              {isEditMode
                ? 'Update your card information below.'
                : 'Add your card to unlock content with a single tap'}
            </p>

            {/* Billing Address Form */}
            <BillingAddressForm
              form={form}
              shouldShowError={shouldShowError}
              isEditMode={isEditMode}
              initialCountry={isEditMode && editCardData ? editCardData.country : null}
              initialState={isEditMode && editCardData ? editCardData.state : null}
              accountData={accountData}
            />

            {/* Card Details Form */}
            {!isEditMode && (
              <CardDetailsForm
                form={form}
                shouldShowError={shouldShowError}
              />
            )}

            {/* Hidden Fields */}
            {!isEditMode && (
              <>
                <input
                  type="hidden"
                  name="ipAddress"
                  data-ccbill="ipAddress"
                  value={watchedValues.ipAddress}
                />
              </>
            )}

            {/* Action Buttons */}
            <div className="payment-actions">
              {message && (
                <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="submit-button"
              >
                {loading ? (isEditMode ? 'Updating...' : 'Processing...') : (isEditMode ? 'Update Card' : 'Add Card')}
              </button>

              <button
                type="button"
                className="cm-back-button"
                onClick={handleCloseButton}
              >
                Back
              </button>

              {/* Payment Logos */}
              <div id="payment-logos" className="payment-logos">
                <PaymentLogos />
              </div>

            </div>

            {/* Footer */}
            <div className="payment-footer">
              <p>
                <i className="fa-solid fa-shield-halved"></i>
                Secure payment powered by CCBill
              </p>
            </div>
          </form>
        </Form>
      </div>
    </Modal>
  );
};

export default AddCardModal; 
